import { useState } from 'react';
import { ProductReview } from '../../../mocks/productReviews';

interface ReviewCardProps {
  review: ProductReview;
  isNew?: boolean;
}

function StarRow({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'xs' }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className={`flex items-center justify-center ${size === 'sm' ? 'w-4 h-4' : 'w-3 h-3'} ${
            i < Math.floor(rating) ? 'text-amber-400' : i < rating ? 'text-amber-300' : 'text-gray-200'
          }`}
        >
          <i className={`ri-star-fill ${size === 'sm' ? 'text-sm' : 'text-xs'}`} />
        </div>
      ))}
    </div>
  );
}

export default function ReviewCard({ review, isNew }: ReviewCardProps) {
  const [helpfulCount, setHelpfulCount] = useState(review.helpful);
  const [voted, setVoted] = useState(false);

  const handleHelpful = () => {
    if (voted) return;
    setHelpfulCount((c) => c + 1);
    setVoted(true);
  };

  return (
    <div className={`bg-white rounded-2xl border p-5 transition-all ${isNew ? 'border-[#FF6B35]/40 bg-[#FFF9F6]' : 'border-gray-100'}`}>
      {isNew && (
        <span className="inline-block bg-[#FF6B35] text-white text-xs font-bold px-2.5 py-0.5 rounded-full mb-3">
          Just posted
        </span>
      )}
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-[#FF6B35] to-[#e85d2a]">
            {review.avatar ? (
              <img src={review.avatar} alt={review.userName} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white font-bold text-sm">
                {review.userName.charAt(0)}
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-gray-900">{review.userName}</p>
              {review.verified && (
                <span className="flex items-center gap-0.5 text-green-600 text-xs font-medium">
                  <div className="w-3 h-3 flex items-center justify-center">
                    <i className="ri-verified-badge-fill text-xs" />
                  </div>
                  Verified
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400">{review.date}</p>
          </div>
        </div>
        <StarRow rating={review.rating} />
      </div>

      {review.title && (
        <p className="text-sm font-bold text-gray-900 mb-1.5">{review.title}</p>
      )}
      <p className="text-sm text-gray-600 leading-relaxed">{review.comment}</p>

      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100">
        <span className="text-xs text-gray-400">Helpful?</span>
        <button
          onClick={handleHelpful}
          className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
            voted
              ? 'bg-green-50 border-green-200 text-green-600'
              : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-[#FF6B35] hover:text-[#FF6B35]'
          }`}
        >
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className={`${voted ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} text-xs`} />
          </div>
          Yes ({helpfulCount})
        </button>
      </div>
    </div>
  );
}
